import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Json } from '@/lib/database.types';
import {
  GRID_SETTING_KEYS,
  loadGridSettings,
  saveGridSetting,
} from '@/shared/components/Grid';
import { Icon } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';
import { canEditTheme, useOrg } from '@/shared/org';
import {
  applyThemeColors,
  contrastRatio,
  effectiveColor,
  sanitizeThemeColors,
  themeKey,
  upgradeGridColors,
  ThemeSwitch,
  THEME_COLOR_GROUPS,
  THEME_COLOR_ROLES,
  THEME_PRESETS,
  type ThemeColorGroup,
  type ThemeColorMode,
  type ThemeColors,
} from '@/shared/theme';
import { ColorPicker } from './ColorPicker';
import { SheetPreview } from './SheetPreview';
import { ThemeLayoutPanel } from './ThemeLayoutPanel';

/**
 * The organization's palette for the whole site — the section that replaced
 * `GridColorsSection`, which edited twelve values that only repainted cells.
 *
 * Three things drive the shape of the screen:
 *
 *   - **A theme is a partial record.** A role the tenant never touched is
 *     absent from the record, not stored at its default, so it keeps resolving
 *     from `tokens.css` and keeps following the brand. That is why the picker's
 *     reset (`onChange(null)`) *deletes* the key instead of writing the default
 *     back, and why an untouched swatch is labelled as the default.
 *   - **Both modes are edited at once.** `applyThemeColors` paints whichever
 *     mode the viewer is in, but a tenant choosing colours has to be able to
 *     see what their dark-mode users will get without switching themes, so
 *     every role shows a light and a dark swatch side by side.
 *   - **Autosave, with the preview already applied.** Carried over from the old
 *     section: every change is injected immediately and written in the
 *     background, because a "Save" button on a screen whose whole point is
 *     seeing the result is a step with nothing behind it.
 */

/** The mode columns, in the order they are shown. */
const MODES: readonly { mode: ThemeColorMode; labelKey: MessageKey }[] = [
  { mode: 'light', labelKey: 'settings.themeColors.modeLight' },
  { mode: 'dark', labelKey: 'settings.themeColors.modeDark' },
];

const GROUP_LABELS: Readonly<Record<ThemeColorGroup, MessageKey>> = {
  chrome: 'settings.themeColors.group.chrome',
  document: 'settings.themeColors.group.document',
  status: 'settings.themeColors.group.status',
};

/**
 * The pairs whose contrast the tenant can actually break by picking colours.
 * Only foreground-on-background combinations that the app really renders are
 * listed — warning about a pair the UI never puts together would train people
 * to ignore the warning.
 */
const CONTRAST_PAIRS: readonly { fg: string; bg: string }[] = [
  { fg: 'topbar_text', bg: 'topbar' },
  { fg: 'text', bg: 'background' },
  { fg: 'muted', bg: 'background' },
  { fg: 'link', bg: 'background' },
  { fg: 'header_text', bg: 'header_fill' },
  { fg: 'group_text', bg: 'group_fill' },
];

/** WCAG AA for body text. AGENTS.md requires it in both themes. */
const AA_RATIO = 4.5;

/** The status group is advanced: rarely touched, so it starts folded away. */
const INITIALLY_OPEN: readonly ThemeColorGroup[] = ['chrome', 'document'];

const roleLabelKey = (base: string): MessageKey | null =>
  THEME_COLOR_ROLES.find((role) => role.base === base)?.labelKey ?? null;

export function ThemeColorsSection() {
  const { t, formatNumber } = useI18n();
  const org = useOrg();

  const [colors, setColors] = useState<ThemeColors>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<readonly ThemeColorGroup[]>(INITIALLY_OPEN);

  const orgId = org.status === 'ready' ? org.orgId : null;
  // `canEditTheme` mirrors `private.has_capability(org, 'theme')`; RLS is the
  // enforcement. Viewing-as is excluded because the database refuses those
  // writes outright (`private.prevent_view_as_write`).
  const editable =
    org.status === 'ready' && !org.isViewingAs && canEditTheme(org.role);

  useEffect(() => {
    if (!orgId) {
      setColors({});
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const settings = await loadGridSettings(orgId);
        if (!active) return;
        const stored = settings[GRID_SETTING_KEYS.theme];
        const envelope =
          stored && typeof stored === 'object' && 'custom' in stored
            ? (stored as { custom: unknown }).custom
            : stored;
        // No theme row *at all*: show the tenant's existing twelve grid colours
        // already filled in, so upgrading looks like a widening of the screen
        // rather than a reset of everything they had chosen. An existing but
        // empty row is a deliberate "restore defaults" and must not resurrect
        // the legacy palette on the next reload.
        const value =
          stored === undefined || stored === null
            ? upgradeGridColors(settings[GRID_SETTING_KEYS.colors])
            : sanitizeThemeColors(envelope);
        setColors(value);
        applyThemeColors(value);
      } catch {
        if (active) setError(t('settings.themeColors.loadError'));
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [orgId, t]);

  const save = useCallback(
    async (next: ThemeColors) => {
      if (!orgId) return;
      setError(null);
      const failure = await saveGridSetting(
        orgId,
        GRID_SETTING_KEYS.theme,
        { custom: next } as unknown as Json,
      );
      if (failure) setError(t('settings.themeColors.saveError'));
    },
    [orgId, t],
  );

  /** Apply and persist in one step — the preview is the state, not a copy. */
  const commit = useCallback(
    (next: ThemeColors) => {
      setColors(next);
      applyThemeColors(next);
      void save(next);
    },
    [save],
  );

  const setRole = useCallback(
    (mode: ThemeColorMode, base: string, hex: string | null) => {
      const key = themeKey(mode, base);
      const next: Record<string, string> = { ...colors };
      // `null` means "stop overriding": deleting the key is what sends the role
      // back to the token, and writing the default hex instead would freeze it.
      if (hex === null) delete next[key];
      else next[key] = hex;
      commit(next);
    },
    [colors, commit],
  );

  const restoreDefaults = useCallback(() => {
    commit({});
  }, [commit]);

  const toggleGroup = useCallback((group: ThemeColorGroup) => {
    setOpen((current) =>
      current.includes(group)
        ? current.filter((g) => g !== group)
        : [...current, group],
    );
  }, []);

  /** Every colour the tenant already chose, offered as the picker's recents. */
  const usedColors = useMemo(() => Object.values(colors), [colors]);

  const matchedPreset = useMemo(
    () =>
      THEME_PRESETS.find((preset) => {
        const keys = Object.keys(preset.colors);
        return (
          keys.length === Object.keys(colors).length &&
          keys.every((key) => colors[key] === preset.colors[key])
        );
      }) ?? null,
    [colors],
  );

  /**
   * The warning for one swatch, or `null`. Keyed off the foreground role, so a
   * pair is reported once — on the colour whose change would fix it.
   */
  const contrastWarning = (mode: ThemeColorMode, base: string): string | null => {
    const pair = CONTRAST_PAIRS.find((p) => p.fg === base);
    if (!pair) return null;
    const ratio = contrastRatio(
      effectiveColor(colors, mode, pair.fg),
      effectiveColor(colors, mode, pair.bg),
    );
    if (ratio >= AA_RATIO) return null;
    const againstKey = roleLabelKey(pair.bg);
    if (!againstKey) return null;
    return t('settings.themeColors.contrastWarning', {
      ratio: formatNumber(ratio, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
      against: t(againstKey),
    });
  };

  if (org.status !== 'ready' || loading) {
    return <p className="text-sm text-muted">{t('common.loading')}</p>;
  }

  return (
    <section className="flex min-w-0 flex-col gap-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-extrabold text-fg">
          {t('settings.themeColors.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {t('settings.themeColors.description')}
        </p>
      </div>

      {error && (
        <div className="rounded-xl bg-danger/10 p-3 text-sm text-danger">{error}</div>
      )}

      {!editable && (
        <p className="rounded-xl border border-border bg-surface-2 p-3 text-sm text-muted">
          {t('settings.themeColors.ownerOnly')}
        </p>
      )}

      {/* The viewer's own light/dark preference — not part of the palette, but
          it decides which of the two columns below they are looking at. */}
      <div className="min-w-0">
        <h2 className="mb-2 text-sm font-bold text-muted">
          {t('settings.themeColors.themeTitle')}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <ThemeSwitch className="w-auto" />
          <span className="min-w-0 text-xs text-muted">
            {t('settings.themeColors.themeAutoHint')}
          </span>
        </div>
      </div>

      <div className="min-w-0">
        <h2 className="mb-2 text-sm font-bold text-muted">
          {t('settings.themeColors.presetsTitle')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {THEME_PRESETS.map((preset) => (
            <PresetSwatch
              key={preset.nameKey}
              label={t(preset.nameKey)}
              colors={preset.colors}
              selected={matchedPreset === preset}
              disabled={!editable}
              onSelect={() => commit(preset.colors)}
            />
          ))}
          {/* "Custom" is not a preset one can pick: it is what the current
              record already is when it matches none of them. */}
          {matchedPreset === null && Object.keys(colors).length > 0 && (
            <PresetSwatch
              label={t('settings.theme.preset.custom')}
              colors={colors}
              selected
              disabled
            />
          )}
        </div>
      </div>

      {THEME_COLOR_GROUPS.map((group) => {
        const expanded = open.includes(group);
        const roles = THEME_COLOR_ROLES.filter((role) => role.group === group);
        return (
          <div
            key={group}
            className="min-w-0 overflow-hidden rounded-2xl border border-border bg-surface"
          >
            <button
              type="button"
              onClick={() => toggleGroup(group)}
              aria-expanded={expanded}
              className="tap-target flex w-full min-w-0 items-center justify-between gap-2 px-4 text-left"
            >
              <span className="min-w-0 truncate text-sm font-bold text-fg">
                {t(GROUP_LABELS[group])}
              </span>
              <Icon
                name={expanded ? 'chevronDown' : 'chevronRight'}
                className="h-4 w-4 shrink-0 text-muted"
              />
            </button>

            {expanded && (
              <div className="flex flex-col gap-2 border-t border-border p-3">
                {roles.map((role) => (
                  <div
                    key={role.base}
                    className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-border bg-surface-2 px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 break-words text-sm text-fg">
                      {t(role.labelKey)}
                    </span>
                    <div className="flex shrink-0 items-start gap-2">
                      {MODES.map(({ mode, labelKey }) => {
                        const chosen = colors[themeKey(mode, role.base)] ?? null;
                        const shown = effectiveColor(colors, mode, role.base);
                        return (
                          <div
                            key={mode}
                            className="flex w-16 min-w-0 flex-col items-center gap-0.5"
                          >
                            <span className="text-[0.625rem] font-bold uppercase text-muted">
                              {t(labelKey)}
                            </span>
                            <ColorPicker
                              valor={chosen}
                              disabled={!editable}
                              ariaLabel={t(role.labelKey)}
                              {...(chosen
                                ? { title: t('settings.themeColors.resetRole') }
                                : {})}
                              onChange={(hex) => setRole(mode, role.base, hex)}
                              coloresUsados={usedColors}
                              trigger={
                                <span className="tap-target flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg border border-border">
                                  <span
                                    className="block h-6 w-6 rounded border border-border"
                                    style={{ background: shown }}
                                  />
                                </span>
                              }
                            />
                            <span className="h-3.5 w-full truncate text-center text-[0.625rem] text-muted">
                              {chosen ? '' : t('settings.themeColors.usingDefault')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {MODES.map(({ mode, labelKey }) => {
                      const warning = contrastWarning(mode, role.base);
                      if (!warning) return null;
                      return (
                        <p
                          key={mode}
                          className="w-full min-w-0 break-words text-xs text-warning"
                        >
                          {`${t(labelKey)} · ${warning}`}
                        </p>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {orgId && <ThemeLayoutPanel orgId={orgId} editable={editable} />}

      {/* Preview. The injected stylesheet already repaints the real UI for the
          viewer's own mode, so these mock-ups exist to show the OTHER one. */}
      <div className="min-w-0">
        <h2 className="mb-2 text-sm font-bold text-muted">
          {t('settings.themeColors.preview')}
        </h2>
        <div className="grid min-w-0 gap-4 lg:grid-cols-2">
          {MODES.map(({ mode, labelKey }) => (
            <div key={mode} className="min-w-0">
              <p className="mb-1 text-xs font-bold text-muted">{t(labelKey)}</p>
              <div className="flex min-w-0 flex-col gap-3">
                <ChromePreview colors={colors} mode={mode} />
                <SheetPreview
                  alineacion={PREVIEW_ALIGNMENT}
                  colores={colors}
                  modo={mode}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!editable}
          onClick={restoreDefaults}
          className="tap-target rounded-xl border border-border bg-surface px-4 text-sm font-bold text-fg transition-colors hover:bg-surface-3 disabled:opacity-60"
        >
          {t('settings.themeColors.restoreDefaults')}
        </button>
      </div>
    </section>
  );
}

/** A preset as three stacked bands — the idiom the old colours screen used. */
function PresetSwatch({
  label,
  colors,
  selected,
  disabled,
  onSelect,
}: {
  label: string;
  colors: ThemeColors;
  selected: boolean;
  disabled: boolean;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      title={label}
      className={`flex flex-col items-center gap-1 rounded-xl border-2 p-1.5 transition-colors disabled:opacity-60 ${
        selected ? 'border-accent' : 'border-border hover:border-accent'
      }`}
    >
      <span className="flex w-16 flex-col overflow-hidden rounded">
        {(['topbar', 'background', 'header_fill'] as const).map((base) => (
          <span
            key={base}
            className="h-3"
            style={{ background: effectiveColor(colors, 'light', base) }}
          />
        ))}
      </span>
      <span className="max-w-16 truncate text-[0.6875rem] font-medium text-fg">{label}</span>
    </button>
  );
}

/**
 * A miniature of the site chrome. The grid preview below it only shows the
 * document roles, and the top bar, page background and links are exactly the
 * ones a tenant is most likely to get wrong.
 */
function ChromePreview({
  colors,
  mode,
}: {
  colors: ThemeColors;
  mode: ThemeColorMode;
}) {
  const { t } = useI18n();
  const color = (base: string) => effectiveColor(colors, mode, base);
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: color('border'), background: color('background') }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ background: color('topbar'), color: color('topbar_text') }}
      >
        <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: color('highlight') }} />
        <span className="min-w-0 truncate text-xs font-bold">
          {t('settings.themeColors.title')}
        </span>
      </div>
      <div className="p-3">
        <p className="min-w-0 break-words text-xs font-bold" style={{ color: color('text') }}>
          {t('settings.themeColors.preview')}
        </p>
        <p className="mt-1 min-w-0 break-words text-[0.6875rem]" style={{ color: color('muted') }}>
          {t('settings.themeColors.description')}
        </p>
        <p className="mt-1 text-[0.6875rem] underline" style={{ color: color('link') }}>
          {t('settings.themeColors.restoreDefaults')}
        </p>
      </div>
    </div>
  );
}

/**
 * The preview grid is about colour, not alignment, so it is rendered with the
 * neutral defaults rather than the organization's alignment setting.
 */
const PREVIEW_ALIGNMENT = {
  header: { align: 'left' as const, wrap: true, lines: 4 },
  text: { align: 'left' as const, wrap: true, lines: 6 },
  chip: { align: 'left' as const, wrap: true, lines: 6 },
  number: { align: 'right' as const, wrap: true, lines: 6 },
  date: { align: 'right' as const, wrap: true, lines: 6 },
  smartchip: { align: 'left' as const, wrap: true, lines: 6 },
  autofill: { align: 'left' as const, wrap: true, lines: 6 },
  checkbox: { align: 'center' as const, wrap: false, lines: 1 },
  attachment: { align: 'left' as const, wrap: false, lines: 1 },
  barcode: { align: 'left' as const, wrap: false, lines: 1 },
  text_document: { align: 'left' as const, wrap: true, lines: 6 },
  pivot_table: { align: 'left' as const, wrap: false, lines: 1 },
  reconciliation: { align: 'left' as const, wrap: false, lines: 1 },
};
